// @flow
import * as React from 'react';
import { change, formValueSelector, reduxForm } from 'redux-form';
import { connect } from 'react-redux';
import styled from 'styled-components';
import moment from "moment/moment";
import { withRouter } from 'react-router-dom';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import bindAll from 'lodash/bindAll';
import { type TRound } from '../../../modules/types';
import * as actions from '../../../modules/actions';
import { getLeagueFinalsRounds, getStartDraftDate } from "../../../helpers";
import Settings from '../../../components/Icons/Settings';
import People from '../../../components/Icons/People';
import Ball from '../../../components/Icons/Ball';
import Trade from '../../../components/Icons/Trade';
import ArrowDown from '../../../components/Icons/ArrowDown';
import ArrowUp from '../../../components/Icons/ArrowUp';

import { ButtonPrimary } from '../../../components/index';
import { leagueValidate } from "../../../utils/FormValidations/index";
import { BorderedIcon, FormUploadImage } from "../../../components";
import colors from '../../../assets/css/colors';
import { below } from "../../../assets/css/media";
import { LeagueSettings } from './forms/LeagueSettings';
import { AdvancedLeagueSettings } from './forms/AdvancedLeagueSettings';
import { GameplaySettings } from './forms/GameplaySettings';
import { DraftSettings } from './forms/DraftSettings';
import { TradersSettings } from './forms/TradersSettings';


const CreateForm = styled.form`
	box-shadow: 0px 0px 6px 3px #e4e4e4;
	margin: 1em 0;

	button {
		display: block;
		width: 290px;
		margin: 0 auto;
	}

	svg {
		width: 1.1em;
	}
`;

const FormItem = styled.div`
	margin: 0 8em;

	label {
		margin: 0 0 0.2em;
	}

	${below.desktop`
		margin: 0 1em;
	`}
`;

const SubTitle = styled.h4`
	display: flex;
	align-items: center;
	color: ${colors.darkGrey};
	font-weight: bold;
	margin: 1em 0 2em 0;

	div {
		margin-right: .5em;
	}
`;

const AdvancedSettings = styled.p`
	color: ${colors.darkGrey};
	line-height: 1.5;
	font-size: 12px;
	max-width: 315px;
`;

const UploadAvatarWrapper = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	background-color: #FFFFFF;
	height: 180px;
	margin-bottom: 35px;
	box-shadow: 0 1px 0 0 rgba(0,0,0,0.25);
	color: ${colors.nrlDarkGreen};

	picture {
		display: flex;
	    width: 100px;
	    height: 100px;
	}
`;

const LeagueName = styled.h3`
	color: ${colors.nrlDarkGreen};
	margin-top: 1em;
	font-size: 20px;
	line-height: 1.3;
	font-weight: bold;
	opacity: ${({ disabled }) => disabled ? '.15' : '1'};
    max-width: 300px;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const AdvancedSettingsWrapper = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 2em;

	.sub-title {
		margin-bottom: .5em;
	}
`;

const ButtonWrapper = styled.div`
	padding: 1em;
	box-shadow: 0 -1px 0 0 #C0C0C0;

	button {
		text-transform: uppercase;
	}
`;

type Props = {
	handleSubmit: Function,
	onChangeFile: Function,
	previewImg: string,
	league_name: string,
	is_show_advanced_settings: boolean,
	toggleForm: Function,
	submitting: boolean,
	is_finals: boolean,
	league_size: number,
	is_draft_commissioner: boolean,
	maxDate: string,
	rounds: Array<TRound>,
	selected_round: TRound,
	is_traders: boolean,
	regenerated_leagues_by_id: Object,
	match: {
		params: {
			regenerate_league_id?: string
		}
	},
	changeFieldValue: Function,
	fetchRegenerateLeague: typeof actions.leagueDraft.regenerateShowDraftLeague,
	is_final_start_round: boolean;
	finals_format: number;
	final_rounds: Array<{ value: number, text: string }>;
	start_round: number
}

class LeagueCreateForm extends React.Component<Props> {
	constructor(props) {
		super(props);
		bindAll(this, ['populateWithRegenLeague']);
	}

	componentDidMount() {
		const regenerated_leagues_by_id = get(this.props, `match.params.regenerate_league_id`);
		// check if we have a regen league id && if it's in state,
		// YES populate form
		// NO fetch regen league => compDidUpdate will trigger once fetch is complete
		if(
			regenerated_leagues_by_id &&
			regenerated_leagues_by_id in this.props.regenerated_leagues_by_id
		) {
			this.populateWithRegenLeague(regenerated_leagues_by_id);
		}
		else {
			this.props.fetchRegenerateLeague({ id: regenerated_leagues_by_id });
		}
	}

	componentDidUpdate(prev_props: Props) {
		const regenerated_leagues_by_id = get(this.props, `match.params.regenerate_league_id`);

		// detect if there is a regen league id
		if(prev_props.match.params.regenerate_league_id !==
			regenerated_leagues_by_id) {
			// check to see if we have that league in our redux state
			if(regenerated_leagues_by_id &&
				regenerated_leagues_by_id
				in this.props.regenerated_leagues_by_id) {
				this.populateWithRegenLeague(regenerated_leagues_by_id);
			}
		}

		// I will trigger once fetch regen league is done
		if(
			!get(prev_props, `regenerated_leagues_by_id[${regenerated_leagues_by_id}]`) &&
			get(this.props, `regenerated_leagues_by_id[${regenerated_leagues_by_id}]`)
		) {
			this.populateWithRegenLeague(regenerated_leagues_by_id);
		}
		this.checkFinalRound(prev_props);
	}

	checkFinalRound(prev_props: Props) {
		// const prev_final_start_round = get(prev_props, 'finals_start_round_selected');
		// const finals_start_round_selected = get(this.props, 'finals_start_round_selected');
		return;
		// const is_changed = prev_final_start_round !== finals_start_round_selected;
		// if (prev_final_start_round && finals_start_round_selected && is_changed) {
		// 	this.props.changeFieldValue('finals_start_round', finals_start_round_selected);
		// }
	}

	populateWithRegenLeague(id: string) {
		const fields = this.props.regenerated_leagues_by_id[id];

		delete fields.start_round;
		delete fields.draft_start;
		delete fields.regenerated_from;

		this.props.changeFieldValue('regenerated_from', id);
		Object.keys(fields).forEach(field => {
			this.props.changeFieldValue(field, fields[field]);
		});

	}

	render() {
		const { props } = this;

		return (
			<CreateForm onSubmit={props.handleSubmit}>
				<UploadAvatarWrapper>
					<FormUploadImage
						name='league_avatar'
						size="100px"
						onChange={props.onChangeFile}
						preview_img={props.previewImg ? props.previewImg : ''}
					/>
					{!props.league_name ? (
						<LeagueName disabled={true}>...League Name</LeagueName>
					) : (
						<LeagueName>{props.league_name}</LeagueName>
					)}
				</UploadAvatarWrapper>

				<FormItem>
					<LeagueSettings {...props} />

					<AdvancedSettingsWrapper>
						<div>
							<SubTitle
								class-name='sub-title'
								is_inactive={props.is_show_advanced_settings}
							>
								<BorderedIcon background='#C0C0C0' hide_border={true}>
									<Settings color='#ffffff' size="2" />
								</BorderedIcon>
							Advanced Settings
							</SubTitle>
							<AdvancedSettings>
							You can create a league via the basic settings
							above or fine tune your Draft League with our advanced settings.
							</AdvancedSettings>
						</div>
						<BorderedIcon background='#ffffff' hide_border={true} size="2.5">
							{!props.is_show_advanced_settings ?
								(
									<ArrowDown
										color={colors.darkGrey}
										size="2"
										cursor='pointer'
										onClick={() => {
											props.toggleForm(true);
										}}
									/>
								) : (
									<ArrowUp
										color={colors.darkGrey}
										size="2"
										cursor='pointer'
										onClick={() => {
											props.toggleForm(false);
										}}
									/>
								)
							}
						</BorderedIcon>
					</AdvancedSettingsWrapper>

				</FormItem>

				{props.is_show_advanced_settings &&
				<FormItem>

					<AdvancedLeagueSettings {...props} />

					<SubTitle>
						<BorderedIcon background='#C0C0C0' hide_border={true}>
							<People color='#ffffff' size="2" />
						</BorderedIcon>
						Draft Options
					</SubTitle>

					<DraftSettings {...props} />

					<SubTitle>
						<BorderedIcon background='#C0C0C0' hide_border={true}>
							<Ball color='#ffffff' size="2" />
						</BorderedIcon>
						Game Play
					</SubTitle>
					<GameplaySettings />
					<SubTitle>
						<BorderedIcon background='#C0C0C0' hide_border={true}>
							<Trade color='#ffffff' size="2" />
						</BorderedIcon>
						Trades & Restricted Free Agents
					</SubTitle>

					<TradersSettings {...props} />

				</FormItem>
				}
				<ButtonWrapper>
					<ButtonPrimary disabled={props.submitting}>
					Create Your League
					</ButtonPrimary>
				</ButtonWrapper>
			</CreateForm>
		);
	}
}

LeagueCreateForm = reduxForm({
	form: 'league_manager',
	validate: leagueValidate,
})(LeagueCreateForm);

const selector = formValueSelector('league_manager');

const FINAL_ROUNDS = {
	'8': 4,
	'6': 3,
	'4': 2,
	'2': 1,
	'0': 0
};

const FINAL_ROUND = Number(process.env.REACT_APP_TOTAL_ROUNDS_SIZE) || 25;

const finalsFormatHelper = actual_round => {
	const remainingRounds = FINAL_ROUND - actual_round;
	const filteredRounds = Object.keys(FINAL_ROUNDS).filter(round => {
		const roundsRequiredToFinish = FINAL_ROUNDS[round];
		return roundsRequiredToFinish < remainingRounds;
	});
	const lastIndex = filteredRounds.length - 1;
	const format = isEmpty(filteredRounds[lastIndex])
		? 0
		: Number(filteredRounds[lastIndex]);

	return format;
};

const mapStateToProps = (state, ownProps) => {
	const actual_round_id = get(ownProps, 'actual_round.id', 25);
	const league_size = selector(state, 'size'),
		default_date = new Date(getStartDraftDate());
	default_date.setMinutes(default_date.getMinutes() + 30);

	const is_finals = selector(state, 'finals') === 1;
	const is_flexible = selector(state, 'play_times') === 'flexible';
	const finals_format = finalsFormatHelper(actual_round_id);
	const finals_start_round = selector(state, 'finals_start_round');
	const field_name = get(state, 'form.league_manager.active', '');
	const start_round = selector(state, 'start_round');

	const { final_rounds, finals_start_round_selected } = getLeagueFinalsRounds({
		finals_format, finals_start_round: parseInt(finals_start_round, 10), field_name,
		selected_round_id: parseInt(start_round, 10), rounds: ownProps.rounds,
	});

	return {
		is_finals,
		is_final_start_round: is_flexible && is_finals,
		is_traders: selector(state, 'trade_waivers') === 1,
		league_size,
		final_rounds,
		finals_format: parseInt(finals_format, 10),
		is_draft_commissioner: selector(state, 'draft_type') === 'commissioner',
		league_name: selector(state, 'name'),
		maxDate: ownProps.selected_round.start,
		regenerated_leagues_by_id: state.leagues.regenerate_show.by_id,
		finals_start_round_selected,
		start_round,
		initialValues: {
			name: '',
			start_round: parseInt(ownProps.selected_round_id, 10),
			privacy: 0,
			play_times: 'flexible',
			finals: 1,
			captains: 1,
			emergency: 1,
			lockout: 1,
			trade_waivers: 1,
			waiver_period: 1,
			waiver_order: 1,
			coaches_box: 1,
			bye_rounds: 0,
			rfa_privacy: 'private',
			approve_trade: 'league',
			size: 16,
			draft_type: 'live',
			finals_format: finalsFormatHelper(actual_round_id),
			finals_start_round: 23,
			draft_start: moment(default_date),
			squad_size: '1-3-2-2-2-3/4',
			draft_order: 'snake',
			draft_turn: 60,
		},
	};
};

const mapDispatchToProps = dispatch => {
	return {
		changeFieldValue: function(field, value) {
			dispatch(change('league_manager', field, value));
		},
		fetchRegenerateLeague: actions.leagueDraft.regenerateShowDraftLeague
	};
};

LeagueCreateForm = withRouter(connect(
	mapStateToProps,
	mapDispatchToProps
)(LeagueCreateForm));

export default LeagueCreateForm;


// WEBPACK FOOTER //
// ./src/pages/leagues/leaguesCreate/LeaguesCreateForm.js